import React, { useState, useEffect } from 'react';
import { UserRole } from '../../types';
import { XMarkIcon, MapPinIcon, InformationCircleIcon, ListIcon, ShareIcon, CalendarDaysIcon, DaviviendaMortgageIcon, DocumentTextIcon, SpinnerIcon } from '../../components/Icons';
import { usePropertyDetailLogic } from '../../hooks/usePropertyDetailLogic';
import { PropertyMediaGallery } from '../../components/broker/detail/PropertyMediaGallery';
import { PropertyOwnerSection } from '../../components/broker/detail/PropertyOwnerSection';
import { PropertyTechnicalGrid } from '../../components/broker/detail/PropertyTechnicalGrid';
import { PropertyPricingCard } from '../../components/broker/detail/PropertyPricingCard';
import { SoftDeleteModal } from '../../components/broker/detail/SoftDeleteModal';
import { PurchaseSimulator } from '../../components/PurchaseSimulator';
import { PropertyLocationMap } from '../../components/PropertyLocationMap';
import { ScheduleVisitModal } from '../../components/broker/ScheduleVisitModal';
import { ClientScheduleVisitModal } from '../../components/client/ClientScheduleVisitModal';
import { CopyFallbackModal } from '../../components/CopyFallbackModal';
import { SimilarPropertiesCarousel } from '../../components/broker/detail/SimilarPropertiesCarousel';
import { PropertyCommentsSection } from '../../components/broker/comments/PropertyCommentsSection';
import { PropertyDetailCommentShortcut } from '../../components/broker/detail/PropertyDetailCommentShortcut';
import { formatSafePropertyText } from '../../utils/propertyValueFormatter';

export const PropertyDetailView: React.FC<any> = ({ property, appUser, inmobiliariasConfig, onClose, onShareProperty, onPropertyUpdate, onViewProperty }) => {
  const logic = usePropertyDetailLogic(property, appUser, inmobiliariasConfig, onPropertyUpdate);
  const [copyModal, setCopyModal] = useState<any>(null);
  const [isVisitOpen, setIsVisitOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  useEffect(() => {
    document.body.classList.add('detail-modal-open');
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.classList.remove('detail-modal-open');
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const p = logic.resolvedProperty;
  const isClient = appUser?.role === UserRole.CLIENTE;

  const handleSalesClick = () => {
    const role = (appUser?.role === UserRole.SUPERADMIN || appUser?.role === UserRole.ADMIN || appUser?.role === UserRole.COMERCIAL_INTERNO) ? 'COMERCIAL' : 'BROKER';
    window.open(`${window.location.origin}/#/proceso-venta?code=${p.codigo}&roleGroup=${role}`, '_blank');
  };

  const fullLocationTitle = [p.neighborhoodName, p.localidad, p.ciudad].filter(Boolean).join(', ');

  if (logic.isLoadingResolved) {
    return (
      <div className="fixed inset-0 bg-gray-900/80 z-[150] flex flex-col items-center justify-center p-4 backdrop-blur-sm" style={{ margin: 0, top: 0 }}>
        <SpinnerIcon className="w-12 h-12 text-sky-400 mb-4" />
        <p className="text-white font-bold uppercase tracking-widest text-xs">Cargando información actualizada...</p>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-2 overflow-hidden" style={{ margin: 0, top: 0 }}>
      <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-sm cursor-pointer" onClick={onClose} />

      {!logic.isDeleted && appUser && !isClient && (
          <button onClick={handleSalesClick} className="fixed right-0 top-1/2 -translate-y-1/2 z-[160] bg-gray-900 text-white px-5 py-6 rounded-l-3xl shadow-2xl flex flex-col items-center gap-3 hover:bg-black transition-all group animate-fade-in-right border-l-4 border-sky-400">
              <DocumentTextIcon className="w-7 h-7 text-sky-400 group-hover:scale-110 transition-transform" />
              <span className="[writing-mode:vertical-lr] rotate-180 text-[10px] font-black uppercase tracking-[0.3em]">Proceso de Venta</span>
          </button>
      )}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full md:w-[94vw] lg:w-[92vw] xl:max-w-7xl max-h-[96vh] flex flex-col overflow-hidden animate-fade-in-up z-10">
        <header className="sticky top-0 bg-white px-6 py-5 border-b border-gray-100 flex justify-between items-center z-20">
            <div className="flex items-center gap-3 min-w-0">
                <h2 className="text-xl font-black truncate text-gray-800 tracking-tight">{p.tipoinmueble} en {p.ciudad}</h2>
                {p.status && p.status !== 'Activo' && (
                  <span className="px-2 py-0.5 text-[9px] font-black uppercase rounded-md bg-amber-100 text-amber-800 border border-amber-200">{p.status}</span>
                )}
                <div onClick={() => setCopyModal({ text: logic.displayCode, title: 'Copiar Código' })} className="hidden sm:block text-[10px] font-black bg-gray-50 px-2.5 py-1.5 rounded-lg text-gray-400 cursor-pointer hover:bg-gray-100 border border-gray-100">CÓD: {logic.displayCode}</div>
            </div>
            <div className="flex items-center gap-2">
                <PropertyDetailCommentShortcut propertyCode={p.codigo} appUser={appUser} />
                {!logic.isDeleted && logic.canDelete && <button onClick={() => setIsDeleteOpen(true)} className="px-4 py-2 bg-red-600 text-white text-[10px] font-black uppercase rounded-lg shadow-lg hover:bg-red-700 transition-all">Bajar Inmueble</button>}
                <button onClick={onClose} className="p-2 rounded-full text-gray-400 hover:text-red-500 transition-colors"><XMarkIcon className="w-6 h-6" /></button>
            </div>
        </header>
        <main className="flex-grow p-4 sm:p-6 overflow-y-auto space-y-8 sm:space-y-10 custom-scrollbar">
          <PropertyMediaGallery validImages={logic.media.validImages} mainVideo={logic.media.mainVideo} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 items-start">
              <div className="md:col-span-2 space-y-12">
                  <section>
                    <h1 className="text-4xl font-black text-gray-900 mb-2 tracking-tighter uppercase">{fullLocationTitle}</h1>
                    <div className="flex items-center text-gray-500 mb-8"><MapPinIcon className="w-5 h-5 mr-2 text-sky-600" /><span className="text-sm font-bold uppercase tracking-tight">{p.direccion}</span></div>
                    <div className="grid grid-cols-4 gap-4 border-y border-gray-100 py-8">
                        {[{l:'Área',v:p.area_construida_original},{l:'Hab.',v:p.num_habitaciones},{l:'Baños',v:p.num_banos},{l:'Estrato',v:p.estrato}].map(a=>(<div key={a.l} className="flex flex-col items-center text-center"><span className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">{a.l}</span><span className="text-sm font-black text-gray-800">{formatSafePropertyText(a.v, '--')}</span></div>))}
                    </div>
                  </section>
                  <section>
                    <div className="flex items-center mb-6"><InformationCircleIcon className="w-5 h-5 mr-2 text-sky-700" /><h3 className="text-lg font-black uppercase tracking-tighter text-[#00234F]">Detalle del Inmueble</h3></div>
                    <PropertyTechnicalGrid property={p} appUser={appUser} />
                  </section>
                  <PropertyOwnerSection property={p} appUser={appUser} />
                  {p.descripcion && (
                    <section><div className="flex items-center mb-4"><ListIcon className="w-5 h-5 mr-2 text-sky-700" /><h3 className="text-lg font-black uppercase tracking-tighter text-[#00234F]">Descripción</h3></div><p className="text-sm text-gray-600 bg-gray-50/50 p-8 rounded-3xl border border-gray-100 shadow-sm whitespace-pre-wrap leading-relaxed">{p.descripcion}</p></section>
                  )}
                  {p.latitude && p.longitude && <section><PropertyLocationMap latitude={p.latitude} longitude={p.longitude} propertyType={p.tipoinmueble} address={p.direccion}/><div className="mt-12"><PurchaseSimulator initialValue={p.precio_venta} /></div></section>}
                  
                  <SimilarPropertiesCarousel 
                    currentPropertyCode={p.codigo} 
                    appUser={appUser} 
                    inmobiliariasConfig={inmobiliariasConfig}
                    onViewProperty={onViewProperty || (() => {})}
                    onShareProperty={onShareProperty}
                  />
                  <PropertyCommentsSection propertyCode={p.codigo} appUser={appUser} />
              </div>
              <PropertyPricingCard
                precio={p.precio_venta}
                admin={p.valor_administracion}
                rate={logic.resolvedRate}
                value={logic.resolvedValue}
                appUser={appUser}
                canReferCredit={logic.canReferCredit}
                commissionTiers={p.tabla_comisiones}
              />
          </div>
        </main>
        <footer className="sticky bottom-0 bg-white px-6 py-5 border-t border-gray-100 flex flex-wrap justify-end items-center gap-3 z-20 shadow-2xl">
            {!logic.isDeleted && appUser && (
                <><button onClick={() => onShareProperty(p)} className="px-6 py-2.5 border-2 border-sky-600 text-sky-600 text-[10px] font-black uppercase rounded-xl hover:bg-sky-50 flex items-center gap-2 transition-all"><ShareIcon className="w-4 h-4" />Compartir</button>
                {appUser.role !== UserRole.COMERCIAL_INTERNO && (<>
                {logic.canReferCredit && !isClient && (
                    <button onClick={() => window.open(`${window.location.origin}/#/referido-credito?code=${p.codigo}&lock=true`, '_blank')} className="px-6 py-2.5 bg-pink-600 text-white text-[10px] font-black uppercase rounded-xl shadow-lg flex items-center gap-2 active:scale-95 transition-all"><DaviviendaMortgageIcon className="w-4 h-4" />Referido Crédito</button>
                )}
                <button onClick={() => setIsVisitOpen(true)} className="px-6 py-2.5 bg-green-600 text-white text-[10px] font-black uppercase rounded-xl shadow-lg flex items-center gap-2 active:scale-95 transition-all"><CalendarDaysIcon className="w-5 h-5" />Agendar Visita</button></>)}</>
            )}
            <button onClick={onClose} className="px-8 py-2.5 text-[10px] font-black uppercase text-gray-400 hover:text-gray-600 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all">Cerrar</button>
        </footer>
      </div>
      {isVisitOpen && (isClient ? (
        <ClientScheduleVisitModal isOpen={isVisitOpen} onClose={() => setIsVisitOpen(false)} property={p} appUser={appUser} />
      ) : (
        <ScheduleVisitModal isOpen={isVisitOpen} onClose={() => setIsVisitOpen(false)} property={p} appUser={appUser} />
      ))}
      {isDeleteOpen && <SoftDeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} isDeleting={logic.isDeleting} code={p.codigo} onConfirm={async (n,r) => { const s = await logic.handleSoftDelete(n,r); if(s) setIsDeleteOpen(false); return s; }} />}
      {copyModal && <CopyFallbackModal isOpen={true} onClose={() => setCopyModal(null)} textToCopy={copyModal.text} title={copyModal.title} />}
    </div>
  );
};
